import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Phone, MessageSquare, Mail, Activity, X, PhoneOff, Plus, Search, Link2,
  CalendarClock, PenLine, PhoneIncoming, HelpCircle, MailCheck, ChevronDown, ChevronUp, Radio, UserCircle2
} from "lucide-react";
import { useAuth } from "./App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_TYPES = [
  "Call Placed", "Call Answered", "Left Voicemail", "Sent SMS", "Sent Email",
  "Added Comment", "Updated Appointment", "Contact Attempt", "Unidentified Voicemail",
  "Incoming Call", "Inspector Help", "Respond Safeguard Email", "Manual Action",
];

const ACTION_ICONS = {
  "Call Placed": Phone, "Call Answered": Phone, "Left Voicemail": PhoneOff,
  "Sent SMS": MessageSquare, "Sent Email": Mail, "Added Comment": PenLine,
  "Updated Appointment": CalendarClock, "Contact Attempt": PhoneIncoming,
  "Unidentified Voicemail": PhoneOff, "Incoming Call": PhoneIncoming,
  "Inspector Help": HelpCircle, "Respond Safeguard Email": MailCheck,
  "Manual Action": Activity,
};

const ACTION_COLORS = {
  "Call Placed": "text-[#2F5D8A]", "Call Answered": "text-[#4A7A56]", "Left Voicemail": "text-[#7A3B3B]",
  "Sent SMS": "text-[#9A7A3E]", "Sent Email": "text-[#5A4F7A]", "Added Comment": "text-[#4B5563]",
  "Updated Appointment": "text-[#2F5D8A]", "Contact Attempt": "text-[#9A7A3E]",
  "Unidentified Voicemail": "text-[#7A3B3B]", "Incoming Call": "text-[#2F5D8A]",
  "Inspector Help": "text-[#4A7A56]", "Respond Safeguard Email": "text-[#9A7A3E]",
  "Manual Action": "text-[#4B5563]",
};

const fmt = (isoStr) => {
  if (!isoStr) return "-";
  return new Date(isoStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};
const fmtDate = (isoStr) => {
  if (!isoStr) return "-";
  const d = new Date(isoStr);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

const LinkedBadge = ({ linked }) => (
  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${
    linked ? "bg-[#D8E8D4] text-[#4A7A56] border-[#C5D9C1]" : "bg-[#EDE5D6] text-[#7A6535] border-[#DDD3BF]"
  }`} data-testid={linked ? "badge-linked" : "badge-unlinked"}>
    {linked ? "Linked" : "Unlinked"}
  </span>
);

// ═══ Register Activity Modal ═══
// Operator is auto-resolved from the logged-in user (Apr 2026) — everyone
// now has a personal login. The manual Operator dropdown was removed.
const RegisterActivityModal = ({ onClose, onSaved, operators, isVoicemail = false }) => {
  const { user } = useAuth();
  const loggedAs = user?.name || user?.email || "your login";
  const [form, setForm] = useState({
    operator_name: "", action_type: isVoicemail ? "Unidentified Voicemail" : "",
    phone_number: "", order_id: "", order_number: "", result: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.action_type) { setError("Action type is required"); return; }
    if (isVoicemail && !form.phone_number) { setError("Phone number is required for voicemail"); return; }
    setSaving(true); setError(null);
    try {
      // operator_name is intentionally omitted — backend auto-fills from
      // the authenticated user.
      const { operator_name: _omit, ...payload } = form;
      await axios.post(`${API}/operator-activities`, payload);
      onSaved(); onClose();
    }
    catch (e) { setError(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-gray-200 p-5 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()} data-testid="register-activity-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800" data-testid="modal-title">{isVoicemail ? "Register Voicemail" : "Register Call Without Order"}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500"><X className="h-4 w-4" /></button>
        </div>
        <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-[#2F5D8A]/8 border border-[#2F5D8A]/20" data-testid="registered-as-badge">
          <UserCircle2 className="h-4 w-4 text-[#2F5D8A] shrink-0" />
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider text-[#2F5D8A]/80 font-semibold leading-none mb-0.5">Registered as</p>
            <p className="text-sm font-bold text-[#2F5D8A] leading-tight">{loggedAs}</p>
          </div>
        </div>
        {error && <div className="mb-3 px-3 py-2 rounded bg-[#F3ECEC] border border-[#DFC4C4] text-[#8B4545] text-xs" data-testid="modal-error">{error}</div>}
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">Action Type *</label>
            <select value={form.action_type} onChange={e => update("action_type", e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none"
              disabled={isVoicemail} data-testid="activity-action-select">
              <option value="">Select action...</option>
              {ACTION_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">Phone Number {isVoicemail ? "*" : "(optional)"}</label>
            <input type="text" value={form.phone_number} onChange={e => update("phone_number", e.target.value)}
              placeholder="e.g. 5551234567" className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none"
              data-testid="activity-phone-input" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">Result</label>
            <input type="text" value={form.result} onChange={e => update("result", e.target.value)}
              placeholder="e.g. No answer, Appointment set..." className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none"
              data-testid="activity-result-input" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">Notes / Comments</label>
            <textarea value={form.notes} onChange={e => update("notes", e.target.value)} rows={2}
              placeholder="Additional details..." className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none resize-y"
              data-testid="activity-notes-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">Order # (optional)</label>
              <input type="text" value={form.order_number} onChange={e => update("order_number", e.target.value)}
                placeholder="e.g. 353636209" className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none"
                data-testid="activity-order-number-input" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">Order ID (optional)</label>
              <input type="text" value={form.order_id} onChange={e => update("order_id", e.target.value)}
                placeholder="System ID" className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none"
                data-testid="activity-order-id-input" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold rounded-lg" data-testid="activity-cancel-btn">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-[#D1A054] hover:bg-[#B08A3E] text-[#162d4a] text-sm font-semibold rounded-lg disabled:opacity-50 flex items-center gap-1.5"
            data-testid="activity-save-btn">{saving ? "Saving..." : isVoicemail ? "Register Voicemail" : "Register Call"}</button>
        </div>
      </div>
    </div>
  );
};

// ═══ Link Order Modal ═══
const LinkOrderModal = ({ activity, onClose, onLinked }) => {
  const [orderNumber, setOrderNumber] = useState("");
  const [orderId, setOrderId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleLink = async () => {
    if (!orderId && !orderNumber) { setError("Provide an Order # or Order ID"); return; }
    setSaving(true); setError(null);
    try {
      await axios.put(`${API}/operator-activities/${activity.id}/link-order`, { order_id: orderId || "manual", order_number: orderNumber });
      onLinked(); onClose();
    } catch (e) { setError(e.response?.data?.detail || "Failed to link"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-gray-200 p-5 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()} data-testid="link-order-modal">
        <h3 className="text-base font-bold text-[#1F2937] mb-1">Link to Order</h3>
        <p className="text-gray-500 text-xs mb-4">{activity.operator_name} — {activity.action_type}{activity.phone_number && ` — ${activity.phone_number}`}</p>
        {error && <div className="mb-3 px-3 py-2 rounded bg-[#F3ECEC] border border-[#DFC4C4] text-[#8B4545] text-xs">{error}</div>}
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">Order Number</label>
            <input type="text" value={orderNumber} onChange={e => setOrderNumber(e.target.value)} placeholder="e.g. 353636209"
              className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none" data-testid="link-order-number-input" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">Order ID (system)</label>
            <input type="text" value={orderId} onChange={e => setOrderId(e.target.value)} placeholder="MongoDB ObjectId"
              className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none" data-testid="link-order-id-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg">Cancel</button>
          <button onClick={handleLink} disabled={saving}
            className="px-3 py-1.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center gap-1"
            data-testid="link-order-save-btn"><Link2 className="h-3 w-3" /> {saving ? "Linking..." : "Link Order"}</button>
        </div>
      </div>
    </div>
  );
};

// ═══ Header Buttons (exported for Scheduling header) ═══
export const OperatorActivityButtons = ({ onVoicemail, onActivity }) => (
  <>
    <div className="relative" data-testid="register-activity-wrapper">
      <button onClick={onActivity}
        className="px-3 py-1.5 bg-[#D1A054] hover:bg-[#B08A3E] text-[#162d4a] text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
        data-testid="register-activity-btn">
        <Plus className="h-3.5 w-3.5" /> Register Call Without Order
      </button>
      <span className="text-[9px] text-gray-400 absolute -bottom-3.5 right-0 whitespace-nowrap" data-testid="helper-text">Use when no order is identified</span>
    </div>
  </>
);

// ═══ Main Section Component ═══
const OperatorActivitySection = ({ operators }) => {
  const [liveStatus, setLiveStatus] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [unmatchedQueue, setUnmatchedQueue] = useState([]);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showVoicemailModal, setShowVoicemailModal] = useState(false);
  const [linkingActivity, setLinkingActivity] = useState(null);
  const [activeTab, setActiveTab] = useState("live");
  const [filterOperator, setFilterOperator] = useState("");
  const [filterPhone, setFilterPhone] = useState("");
  const [collapsed, setCollapsed] = useState(false);

  const fetchLiveStatus = useCallback(async () => {
    try { setLiveStatus((await axios.get(`${API}/operator-activities/live-status`)).data); }
    catch (e) { console.error(e); }
  }, []);

  const fetchActivityFeed = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterOperator) params.set("operator", filterOperator);
      if (filterPhone) params.set("phone", filterPhone);
      params.set("limit", "100");
      setActivityFeed((await axios.get(`${API}/operator-activities?${params}`)).data);
    } catch (e) { console.error(e); }
  }, [filterOperator, filterPhone]);

  const fetchUnmatched = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterOperator) params.set("operator", filterOperator);
      if (filterPhone) params.set("phone", filterPhone);
      setUnmatchedQueue((await axios.get(`${API}/operator-activities/unmatched?${params}`)).data);
    } catch (e) { console.error(e); }
  }, [filterOperator, filterPhone]);

  const refreshAll = useCallback(() => {
    fetchLiveStatus(); fetchActivityFeed(); fetchUnmatched();
  }, [fetchLiveStatus, fetchActivityFeed, fetchUnmatched]);

  useEffect(() => {
    fetchLiveStatus(); fetchActivityFeed(); fetchUnmatched();
    const interval = setInterval(() => { fetchLiveStatus(); fetchActivityFeed(); fetchUnmatched(); }, 15000);
    return () => clearInterval(interval);
  }, [fetchLiveStatus, fetchActivityFeed, fetchUnmatched]);

  useEffect(() => { fetchActivityFeed(); fetchUnmatched(); }, [filterOperator, filterPhone, fetchActivityFeed, fetchUnmatched]);

  // Expose modal openers via ref or callback — we use a simpler approach with state
  // The parent passes callbacks via the exported OperatorActivityButtons

  return (
    <>
      <div className="bg-white/80 rounded-lg border border-gray-200 overflow-hidden" data-testid="operator-activity-section">
        {/* Section header */}
        <button onClick={() => setCollapsed(c => !c)}
          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
          data-testid="activity-section-toggle">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-[#B08A3E]" />
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Live Operator Activity</span>
            <span className="flex items-center gap-1 text-[10px] text-[#6B8F62]">
              <Radio className="h-2.5 w-2.5 animate-pulse" /> 15s
            </span>
            {unmatchedQueue.length > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#EDE5D6] text-[#7A6535] border border-[#DDD3BF]">
                {unmatchedQueue.length} unmatched
              </span>
            )}
          </div>
          {collapsed ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronUp className="h-4 w-4 text-gray-500" />}
        </button>

        {!collapsed && (
          <div className="px-4 pb-4 space-y-3 border-t border-gray-200">
            {/* Tabs + Filters */}
            <div className="flex items-center justify-between pt-3">
              <div className="flex gap-1">
                {[
                  { id: "live", label: `Live Status (${liveStatus.length})` },
                  { id: "feed", label: `Activity Feed (${activityFeed.length})` },
                  { id: "unmatched", label: `Unmatched (${unmatchedQueue.length})` },
                ].map(t => (
                  <button key={t.id} onClick={() => setActiveTab(t.id)}
                    className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
                      activeTab === t.id ? "bg-[#D1A054] text-[#162d4a]" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`} data-testid={`section-tab-${t.id}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
                  <input type="text" value={filterPhone} onChange={e => setFilterPhone(e.target.value)}
                    placeholder="Phone..." className="pl-7 pr-2 py-1 bg-gray-50 border border-gray-300 rounded text-[11px] text-gray-800 w-28 focus:border-[#D1A054] focus:outline-none"
                    data-testid="filter-phone-input" />
                </div>
                <select value={filterOperator} onChange={e => setFilterOperator(e.target.value)}
                  className="bg-gray-50 border border-gray-300 rounded px-2 py-1 text-[11px] text-gray-800 focus:border-[#D1A054] focus:outline-none"
                  data-testid="filter-operator-select">
                  <option value="">All operators</option>
                  {operators.map(op => <option key={op.name} value={op.name}>{op.name}</option>)}
                </select>
                {(filterPhone || filterOperator) && (
                  <button onClick={() => { setFilterPhone(""); setFilterOperator(""); }}
                    className="text-[10px] text-gray-500 hover:text-gray-800 flex items-center gap-0.5" data-testid="clear-filters-btn">
                    <X className="h-3 w-3" /> Clear
                  </button>
                )}
              </div>
            </div>

            {/* Live Status Tab */}
            {activeTab === "live" && (
              liveStatus.length === 0 ? (
                <div className="text-center py-4 text-gray-400 text-xs" data-testid="no-live-status">No operator activity recorded today</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="live-status-table">
                    <thead>
                      <tr className="border-b border-gray-200">
                        {["Operator", "Last Action", "Time", "Phone", "Order #", "Status", "Today", "Linked", "Unlinked"].map(h => (
                          <th key={h} className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {liveStatus.map(op => {
                        const Icon = ACTION_ICONS[op.last_action] || Activity;
                        return (
                          <tr key={op.operator_name} className="hover:bg-gray-50" data-testid={`live-op-${op.operator_name.replace(/\s+/g, '-')}`}>
                            <td className="px-2.5 py-1.5 text-gray-800 font-medium">{op.operator_name}</td>
                            <td className="px-2.5 py-1.5"><span className="flex items-center gap-1 text-gray-600"><Icon className="h-3 w-3 text-gray-500" /> {op.last_action}</span></td>
                            <td className="px-2.5 py-1.5 text-gray-500 whitespace-nowrap">{fmt(op.last_action_time)}</td>
                            <td className="px-2.5 py-1.5 text-gray-600 font-mono text-[10px]">{op.last_phone || "-"}</td>
                            <td className="px-2.5 py-1.5 text-gray-600">{op.last_order_number || "-"}</td>
                            <td className="px-2.5 py-1.5"><LinkedBadge linked={op.last_linked} /></td>
                            <td className="px-2.5 py-1.5 text-center text-gray-800 font-bold">{op.total_actions}</td>
                            <td className="px-2.5 py-1.5 text-center text-[#4A7A56] font-medium">{op.linked_count}</td>
                            <td className="px-2.5 py-1.5 text-center text-[#9A7A3E] font-medium">{op.unlinked_count}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {/* Activity Feed Tab */}
            {activeTab === "feed" && (
              activityFeed.length === 0 ? (
                <div className="text-center py-4 text-gray-400 text-xs">No activities recorded</div>
              ) : (
                <div className="overflow-x-auto" data-testid="activity-feed-section">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-200">
                        {["Time", "Operator", "Action", "Phone", "Order #", "Result", "Notes", "Status"].map(h => (
                          <th key={h} className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {activityFeed.map(a => {
                        const Icon = ACTION_ICONS[a.action_type] || Activity;
                        return (
                          <tr key={a.id} className={`hover:bg-gray-50 ${!a.linked ? 'border-l-2 border-l-[#D1A054]/50' : ''}`} data-testid={`activity-row-${a.id}`}>
                            <td className="px-2.5 py-1.5 text-gray-500 whitespace-nowrap">{fmtDate(a.timestamp)}</td>
                            <td className="px-2.5 py-1.5 text-gray-800 font-medium">{a.operator_name}</td>
                            <td className="px-2.5 py-1.5"><span className={`flex items-center gap-1 ${ACTION_COLORS[a.action_type] || "text-gray-600"}`}><Icon className="h-3 w-3" /> {a.action_type}</span></td>
                            <td className="px-2.5 py-1.5 text-gray-600 font-mono text-[10px]">{a.phone_number || "-"}</td>
                            <td className="px-2.5 py-1.5 text-gray-600">{a.order_number || "-"}</td>
                            <td className="px-2.5 py-1.5 text-gray-600 max-w-[100px] truncate">{a.result || "-"}</td>
                            <td className="px-2.5 py-1.5 text-gray-500 max-w-[120px] truncate">{a.notes || "-"}</td>
                            <td className="px-2.5 py-1.5"><LinkedBadge linked={a.linked} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {/* Unmatched Queue Tab */}
            {activeTab === "unmatched" && (
              unmatchedQueue.length === 0 ? (
                <div className="text-center py-4 text-gray-400 text-xs">No unmatched activities</div>
              ) : (
                <div className="overflow-x-auto" data-testid="unmatched-queue-section">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-200">
                        {["Time", "Operator", "Action", "Phone", "Result", "Notes", ""].map(h => (
                          <th key={h} className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {unmatchedQueue.map(a => {
                        const Icon = ACTION_ICONS[a.action_type] || Activity;
                        return (
                          <tr key={a.id} className="hover:bg-gray-50 border-l-2 border-l-[#D1A054]/50" data-testid={`unmatched-row-${a.id}`}>
                            <td className="px-2.5 py-1.5 text-gray-500 whitespace-nowrap">{fmtDate(a.timestamp)}</td>
                            <td className="px-2.5 py-1.5 text-gray-800 font-medium">{a.operator_name}</td>
                            <td className="px-2.5 py-1.5 text-[#7A3B3B] flex items-center gap-1"><Icon className="h-3 w-3" /> {a.action_type}</td>
                            <td className="px-2.5 py-1.5 text-gray-600 font-mono text-[10px]">{a.phone_number || "-"}</td>
                            <td className="px-2.5 py-1.5 text-gray-600 max-w-[100px] truncate">{a.result || "-"}</td>
                            <td className="px-2.5 py-1.5 text-gray-500 max-w-[120px] truncate">{a.notes || "-"}</td>
                            <td className="px-2.5 py-1.5">
                              <button onClick={() => setLinkingActivity(a)}
                                className="px-2 py-0.5 bg-[#D8E8D4] hover:bg-[#C5D9C1] border border-[#A7C4A0] text-[#4A7A56] rounded text-[10px] font-semibold flex items-center gap-1"
                                data-testid={`link-btn-${a.id}`}>
                                <Link2 className="h-3 w-3" /> Link
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showRegisterModal && <RegisterActivityModal onClose={() => setShowRegisterModal(false)} onSaved={refreshAll} operators={operators} />}
      {showVoicemailModal && <RegisterActivityModal onClose={() => setShowVoicemailModal(false)} onSaved={refreshAll} operators={operators} isVoicemail />}
      {linkingActivity && <LinkOrderModal activity={linkingActivity} onClose={() => setLinkingActivity(null)} onLinked={refreshAll} />}
    </>
  );
};

// Export setters via a hook-like pattern
export const useOperatorActivityModals = () => {
  const [showRegister, setShowRegister] = useState(false);
  const [showVoicemail, setShowVoicemail] = useState(false);
  return { showRegister, setShowRegister, showVoicemail, setShowVoicemail };
};

export { RegisterActivityModal, LinkOrderModal };
export default OperatorActivitySection;
